import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiFolder, FiFileText, FiShoppingCart, FiBox, FiCheckSquare,
  FiBarChart2, FiMapPin, FiUser, FiClock, FiTrendingUp, FiTrendingDown
} from 'react-icons/fi';
import { MdCurrencyRupee } from 'react-icons/md';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import API from '../api/axios';
import './Dashboard.css';

/* ── tiny sparkline ── */
function Sparkline({ color }) {
  const points = [10,18,12,22,16,24,18,28,20,30,22,26].map((v, i) => `${i * 9},${35 - v}`).join(' ');
  return (
    <svg viewBox="0 0 100 38" className="sparkline" preserveAspectRatio="none">
      <polyline fill="none" stroke={color} strokeWidth="2" points={points} />
    </svg>
  );
}

/* ── donut chart ── */
function DonutChart({ segments, total }) {
  const r = 60, cx = 70, cy = 70, stroke = 18;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#a855f7'];
  return (
    <svg viewBox="0 0 140 140" className="donut-svg">
      {segments.map((seg, i) => {
        const dash = (seg.value / total) * circ;
        const el = (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={colors[i]} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px` }}
          />
        );
        offset += dash;
        return el;
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" className="donut-num">{total}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" className="donut-label">Total Projects</text>
    </svg>
  );
}


export default function Dashboard() {
  const [stats, setStats] = useState({ projects: 0, quotations: 0, payments: 0, purchaseOrders: 0, materials: 0 });
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [siteActivities, setSiteActivities] = useState([]);
  const [payments, setPayments] = useState([]);
  const [overview, setOverview] = useState({ projectValue: 0, received: 0, pending: 0, orders: 0, materialsReceived: 0, tasksCompleted: 0 });
  const [loading, setLoading]   = useState(true);


  useEffect(() => {
    Promise.allSettled([
      API.get('/projects'),
      API.get('/tasks'),
      API.get('/quotations'),
      API.get('/purchase'),
      API.get('/material-planning'),
      API.get('/site-execution'),
    ]).then(([proj, task, quot, po, mat, site]) => {
      const projectData = proj.value?.data || [];
      const taskData = task.value?.data || [];
      const quotData = quot.value?.data || [];
      const poData = po.value?.data || [];
      const matData = mat.value?.data || [];
      const siteData = site.value?.data || [];

      setProjects(projectData);
      setTasks(taskData.slice(0, 5));
      setSiteActivities(siteData.slice(0, 5));

      const totalValue = projectData.reduce((s, p) => s + (p.budget || 0), 0);
      const received = projectData.reduce((s, p) => s + (p.receivedAmount || 0), 0);

      setStats({
        projects: projectData.length,
        quotations: quotData.filter(q => q.status === 'pending').length,
        payments: totalValue - received,
        purchaseOrders: poData.filter(p => p.status === 'pending').length,
        materials: matData.filter(m => m.status === 'pending').length,
      });

      setPayments(projectData.filter(p => (p.budget || 0) - (p.receivedAmount || 0) > 0).slice(0, 4));

      setOverview({
        projectValue: totalValue,
        received,
        pending: totalValue - received,
        orders: poData.length,
        materialsReceived: matData.filter(m => m.status === 'received').length,
        tasksCompleted: taskData.filter(t => t.status === 'completed').length,
      });
    }).finally(() => setLoading(false));
  }, []);

  const statusGroups = [
    { label: 'In Progress', key: 'in-progress' },
    { label: 'Not Started', key: 'not-started' },
    { label: 'On Hold', key: 'on-hold' },
    { label: 'Completed', key: 'completed' },
  ].map(g => ({ ...g, value: projects.filter(p => p.status === g.key).length }));

  const completedTasks = tasks.filter(t => t.status === 'completed').length;

  const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

  const priorityColor = { high: '#ef4444', medium: '#f59e0b', low: '#22c55e' };
  const priorityBg = { high: '#fef2f2', medium: '#fffbeb', low: '#f0fdf4' };

  return (
    <Layout>
      {loading && <PageLoader text="Loading dashboard..." />}
      <div className="db-page">

        {/* ── Stat Cards ── */}
        <div className="db-stats">
          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon" style={{ background: '#eff6ff' }}>
                <FiFolder style={{ color: '#3b82f6' }} />
              </div>
              <div className="db-stat-info">
                <p className="db-stat-label">Active Projects</p>
                <p className="db-stat-num">{stats.projects}</p>
                <p className="db-stat-change up"><FiTrendingUp /> +2 this week</p>
              </div>
            </div>
            <Sparkline color="#3b82f6" />
          </div>
          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon" style={{ background: '#fff7ed' }}>
                <FiFileText style={{ color: '#f97316' }} />
              </div>
              <div className="db-stat-info">
                <p className="db-stat-label">Pending Quotations</p>
                <p className="db-stat-num">{stats.quotations}</p>
                <p className="db-stat-change up"><FiTrendingUp /> +1 this week</p>
              </div>
            </div>
            <Sparkline color="#f97316" />
          </div>
          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon" style={{ background: '#f0fdf4' }}>
                <MdCurrencyRupee style={{ color: '#22c55e', fontSize: '1.2rem' }} />
              </div>
              <div className="db-stat-info">
                <p className="db-stat-label">Pending Payments</p>
                <p className="db-stat-num db-stat-num--sm">{fmt(stats.payments)}</p>
                <p className="db-stat-change down"><FiTrendingDown /> -5% from last week</p>
              </div>
            </div>
            <Sparkline color="#22c55e" />
          </div>
          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon" style={{ background: '#faf5ff' }}>
                <FiShoppingCart style={{ color: '#a855f7' }} />
              </div>
              <div className="db-stat-info">
                <p className="db-stat-label">Purchase Orders Pending</p>
                <p className="db-stat-num">{stats.purchaseOrders}</p>
                <p className="db-stat-change up"><FiTrendingUp /> +2 this week</p>
              </div>
            </div>
            <Sparkline color="#a855f7" />
          </div>
          <div className="db-stat-card">
            <div className="db-stat-top">
              <div className="db-stat-icon" style={{ background: '#fff1f2' }}>
                <FiBox style={{ color: '#ef4444' }} />
              </div>
              <div className="db-stat-info">
                <p className="db-stat-label">Material Pending</p>
                <p className="db-stat-num">{stats.materials}</p>
                <p className="db-stat-change neutral">No change</p>
              </div>
            </div>
            <Sparkline color="#ef4444" />
          </div>
        </div>

        {/* ── Middle Row ── */}
        <div className="db-mid">
          {/* Today's Tasks */}
          <div className="db-card db-tasks">
            <div className="db-card-header">
              <div className="db-card-title"><FiCheckSquare className="db-card-icon" /> Today's Tasks</div>
              <Link to="/task-planner" className="db-view-all">View All</Link>
            </div>
            <div className="db-task-list">
              {tasks.length === 0 ? (
                <p className="db-empty">No tasks for today.</p>
              ) : tasks.map((t, i) => (
                <div key={t._id || i} className="db-task-row">
                  <input type="checkbox" className="db-task-check" defaultChecked={t.status === 'completed'} readOnly />
                  <div className="db-task-body">
                    <p className="db-task-title">{t.title || t.name}</p>
                    <p className="db-task-sub">{t.category || t.type || 'Task'}</p>
                  </div>
                  <span className="db-priority-badge" style={{ background: priorityBg[t.priority?.toLowerCase()] || '#f8fafc', color: priorityColor[t.priority?.toLowerCase()] || '#64748b' }}>
                    {t.priority || 'Normal'}
                  </span>
                  <span className="db-task-time">{t.time || '--:-- AM'}</span>
                </div>
              ))}
            </div>
            <div className="db-card-footer">
              <span>Total Tasks: <strong>{tasks.length}</strong></span>
              <span className="db-footer-green">Completed: <strong>{completedTasks}</strong></span>
            </div>
          </div>

          {/* Today's Site Activities */}
          <div className="db-card db-site">
            <div className="db-card-header">
              <div className="db-card-title"><FiBarChart2 className="db-card-icon" /> Today's Site Activities</div>
              <Link to="/site-execution" className="db-view-all">View All</Link>
            </div>
            <div className="db-site-list">
              {siteActivities.length === 0 ? (
                <p className="db-empty">No site activities today.</p>
              ) : siteActivities.map((s, i) => (
                <div key={s._id || i} className="db-site-row">
                  <div className="db-site-img">
                    {s.image ? <img src={s.image} alt="" /> : <div className="db-site-img-ph" />}
                  </div>
                  <div className="db-site-info">
                    <p className="db-site-id">{s.projectId || `PRJ100${i + 1}`} · {s.title || s.name}</p>
                    <p className="db-site-meta"><FiMapPin /> {s.location || 'N/A'}</p>
                    <p className="db-site-meta"><FiUser /> Supervisor: {s.supervisor || 'N/A'}</p>
                  </div>
                  <div className="db-site-right">
                    <span className={`db-status-badge db-status-${(s.status || 'in-progress').replace(/\s+/g, '-').toLowerCase()}`}>
                      {s.status || 'In Progress'}
                    </span>
                    <span className="db-site-time"><FiClock /> {s.time || '09:00 AM'}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="db-card-footer">
              <span>Total Activities: <strong>{siteActivities.length}</strong></span>
              <span className="db-footer-green">In Progress: <strong>{siteActivities.filter(s => s.status === 'In Progress').length}</strong></span>
              <span className="db-footer-orange">Not Started: <strong>{siteActivities.filter(s => s.status === 'Not Started').length}</strong></span>
            </div>
          </div>

          {/* Right Column */}
          <div className="db-right-col">
            {/* Project Status Overview */}
            <div className="db-card db-status-card">
              <div className="db-card-header">
                <div className="db-card-title"><FiBarChart2 className="db-card-icon" /> Project Status Overview</div>
                <Link to="/projects" className="db-view-all">View Report</Link>
              </div>
              <div className="db-donut-row">
                <DonutChart segments={statusGroups} total={projects.length} />
                <div className="db-legend">
                  {statusGroups.map((g, i) => {
                    const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#a855f7'];
                    const pct = projects.length ? Math.round((g.value / projects.length) * 100) : 0;
                    return (
                      <div key={g.key} className="db-legend-row">
                        <span className="db-legend-dot" style={{ background: colors[i] }} />
                        <span className="db-legend-label">{g.label}</span>
                        <span className="db-legend-count">{g.value} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Pending Payments */}
            <div className="db-card db-payments-card">
              <div className="db-card-header">
                <div className="db-card-title"><MdCurrencyRupee className="db-card-icon" /> Recent Pending Payments</div>
                <Link to="/projects" className="db-view-all">View All</Link>
              </div>
              <div className="db-payment-list">
                {payments.length === 0 ? (
                  <p className="db-empty">No pending payments.</p>
                ) : payments.map((p, i) => {
                  const due = (p.budget || 0) - (p.receivedAmount || 0);
                  return (
                    <div key={p._id || i} className="db-payment-row">
                      <div>
                        <p className="db-payment-name">{typeof p.client === 'object' ? p.client?.name : p.client || p.clientName || 'Client'} · {p.title}</p>
                        <p className="db-payment-proj">{p.projectId || `PRJ100${i + 1}`}</p>
                      </div>
                      <div className="db-payment-right">
                        <p className="db-payment-amount">{fmt(due)}</p>
                        <p className="db-payment-due">{due > 0 ? 'Due in 2 days' : 'Overdue'}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* ── Quick Overview Summary ── */}
        <div className="db-card db-overview">
          <div className="db-card-header">
            <div className="db-card-title"><FiBarChart2 className="db-card-icon" /> Quick Overview Summary</div>
          </div>
          <div className="db-overview-grid">
            <div className="db-ov-item">
              <div className="db-ov-icon" style={{ background: '#eff6ff' }}>💼</div>
              <div>
                <p className="db-ov-val">{fmt(overview.projectValue)}</p>
                <p className="db-ov-label">Total Project Value</p>
              </div>
            </div>
            <div className="db-ov-item">
              <div className="db-ov-icon" style={{ background: '#f0fdf4' }}>💰</div>
              <div>
                <p className="db-ov-val">{fmt(overview.received)}</p>
                <p className="db-ov-label">Total Received</p>
              </div>
            </div>
            <div className="db-ov-item">
              <div className="db-ov-icon" style={{ background: '#fff7ed' }}>📋</div>
              <div>
                <p className="db-ov-val">{fmt(overview.pending)}</p>
                <p className="db-ov-label">Total Pending</p>
              </div>
            </div>
            <div className="db-ov-item">
              <div className="db-ov-icon" style={{ background: '#eff6ff' }}>🛒</div>
              <div>
                <p className="db-ov-val">{overview.orders}</p>
                <p className="db-ov-label">This Month</p>
              </div>
            </div>
            <div className="db-ov-item">
              <div className="db-ov-icon" style={{ background: '#fff7ed' }}>📦</div>
              <div>
                <p className="db-ov-val">{overview.materialsReceived}</p>
                <p className="db-ov-label">This Month</p>
              </div>
            </div>
            <div className="db-ov-item">
              <div className="db-ov-icon" style={{ background: '#f0fdf4' }}>✅</div>
              <div>
                <p className="db-ov-val">{overview.tasksCompleted}</p>
                <p className="db-ov-label">Today</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}
